import { useMemo, useState, type CSSProperties, type ReactNode } from "react";
import { SearchField, Select } from "@legends/ui";
import { monoLabelStyle } from "./sections";

// Uniform search / sort / filter for the back-office list surfaces (ADR 0207). An
// app-level composition of the `@legends/ui` SearchField + Select — the same layer as
// `sections.tsx` (Section/AsyncState), NOT a new design-system primitive. The AG Grid
// screens already get search/sort/filter for free (ADR 0156); this brings the plain
// `.map` lists that can grow long (cave access, events, the platform catalog, operator
// recordings, the global media catalogs) up to the same standard without disturbing
// their card/row layouts. Fan-facing Cave-language storefronts and the real-time live
// consoles are intentionally out of scope.

/** A sort option: a stable key, a self-descriptive label ("Name (A–Z)", "Soonest"), and
 *  a comparator. The comparator sorts a COPY, so it may be non-stable. */
export interface SortSpec<T> {
  key: string;
  label: string;
  cmp: (a: T, b: T) => number;
}

/** A facet filter: a select whose non-empty value narrows the list via `test`. The empty
 *  value ("") is the "all" pass-through (labelled `allLabel`). */
export interface FacetSpec<T> {
  key: string;
  label: string;
  options: { value: string; label: string }[];
  test: (item: T, value: string) => boolean;
  allLabel?: string;
}

export interface ListControlsConfig<T> {
  /** The strings a query is matched against (case-insensitive substring, any field). */
  searchText: (item: T) => (string | null | undefined)[];
  sorts?: SortSpec<T>[];
  facets?: FacetSpec<T>[];
  searchPlaceholder?: string;
  /** Accessible name for the search input (defaults to the placeholder or "Search list"). */
  searchLabel?: string;
}

interface ControlsState {
  query: string;
  sortKey: string;
  facets: Record<string, string>;
}

/**
 * Pure filter + sort — the whole data transform, no React. Filters by the query across
 * `searchText` fields (case-insensitive substring), applies each active facet, then sorts
 * by the selected comparator (on a copy). Exported for unit tests.
 */
export function filterSortItems<T>(
  items: T[],
  cfg: ListControlsConfig<T>,
  state: ControlsState,
): T[] {
  const q = state.query.trim().toLowerCase();
  let out = items;
  if (q) {
    out = out.filter((it) =>
      cfg.searchText(it).some((s) => (s ?? "").toLowerCase().includes(q)),
    );
  }
  for (const f of cfg.facets ?? []) {
    const v = state.facets[f.key];
    if (v) out = out.filter((it) => f.test(it, v));
  }
  const sort = (cfg.sorts ?? []).find((s) => s.key === state.sortKey);
  if (sort) out = [...out].sort(sort.cmp);
  return out;
}

/**
 * The list-controls hook: owns the query/sort/facet state, memoizes the pure transform,
 * and returns a ready-to-render `toolbar` plus the filtered+sorted `result`. Call sites
 * render `{toolbar}` above their existing `.map`, iterating `result` instead of the raw
 * list. The toolbar renders nothing when the pre-filter list is empty (the caller's own
 * empty-state copy covers that).
 */
export function useListControls<T>(
  items: T[],
  cfg: ListControlsConfig<T>,
): { toolbar: ReactNode; result: T[]; sortKey: string } {
  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState(cfg.sorts?.[0]?.key ?? "");
  const [facets, setFacets] = useState<Record<string, string>>({});

  const result = useMemo(
    () => filterSortItems(items, cfg, { query, sortKey, facets }),
    // cfg is static per surface; recompute on the state + data that actually change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, query, sortKey, facets],
  );

  const toolbar =
    items.length === 0 ? null : (
      <ListToolbar
        cfg={cfg}
        query={query}
        sortKey={sortKey}
        facets={facets}
        total={items.length}
        shown={result.length}
        onQuery={setQuery}
        onSort={setSortKey}
        onFacet={(k, v) => setFacets((prev) => ({ ...prev, [k]: v }))}
      />
    );

  // sortKey lets a caller adapt UI to the active sort (e.g. the Events tab shows its
  // ↑/↓ reorder arrows only under the Custom-order view, LSC-572).
  return { toolbar, result, sortKey };
}

const toolbarStyle: CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: 10,
  margin: "10px 0 12px",
};

function ListToolbar<T>({
  cfg,
  query,
  sortKey,
  facets,
  total,
  shown,
  onQuery,
  onSort,
  onFacet,
}: {
  cfg: ListControlsConfig<T>;
  query: string;
  sortKey: string;
  facets: Record<string, string>;
  total: number;
  shown: number;
  onQuery: (v: string) => void;
  onSort: (v: string) => void;
  onFacet: (key: string, v: string) => void;
}) {
  const searchLabel = cfg.searchLabel ?? cfg.searchPlaceholder ?? "Search list";
  return (
    <div style={toolbarStyle}>
      <SearchField
        value={query}
        onChange={onQuery}
        kbd=""
        placeholder={cfg.searchPlaceholder ?? "Search…"}
        aria-label={searchLabel}
        width={240}
      />
      {cfg.sorts && cfg.sorts.length > 1 && (
        <Select
          aria-label="Sort by"
          value={sortKey}
          onChange={onSort}
          options={cfg.sorts.map((s) => ({ value: s.key, label: s.label }))}
        />
      )}
      {(cfg.facets ?? []).map((f) => (
        <Select
          key={f.key}
          aria-label={`Filter by ${f.label.toLowerCase()}`}
          value={facets[f.key] ?? ""}
          onChange={(v) => onFacet(f.key, v)}
          options={[{ value: "", label: f.allLabel ?? `All ${f.label.toLowerCase()}` }, ...f.options]}
        />
      ))}
      <span style={{ ...monoLabelStyle, marginLeft: "auto" }} aria-live="polite">
        {shown === total ? `${total}` : `${shown} of ${total}`}
      </span>
    </div>
  );
}
